import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const discountLabels = { flat: "Flat HK$ Off", percent: "% Off", free_delivery: "Free Delivery" };

const PromoCodeTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [promoCodes, setPromoCodes] = useState([]);

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.promoCodes || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchPromoCodes = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/promo-codes');
            setPromoCodes(data.promoCodes);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load promo codes", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPromoCodes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        try {
            await axios.delete(`/api/v1/admin/promo-code/${id}`);
            enqueueSnackbar("Promo code deleted", { variant: "success" });
            setPromoCodes((prev) => prev.filter((p) => p._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete promo code", { variant: "error" });
        }
    };

    const columns = [
        {
            field: "code",
            headerName: "Code",
            minWidth: 140,
            flex: 0.5,
            renderCell: (params) => <span className="font-mono font-medium">{params.row.code}</span>,
        },
        {
            field: "discountType",
            headerName: "Discount",
            minWidth: 130,
            flex: 0.5,
        },
        {
            field: "usage",
            headerName: "Used / Limit",
            minWidth: 130,
            flex: 0.4,
        },
        {
            field: "expiryDate",
            headerName: "Expires",
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: "active",
            headerName: "Active",
            minWidth: 90,
            flex: 0.3,
            renderCell: (params) => (
                params.row.active
                    ? <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">Active</span>
                    : <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">Hidden</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 100,
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <Actions editRoute={"promo-code"} deleteHandler={deleteHandler} id={params.row.id} name={params.row.code} canEdit={canEdit} canDelete={canDelete} />
            ),
        },
    ];

    const rows = promoCodes.map((p) => ({
        id: p._id,
        code: p.code,
        discountType: discountLabels[p.discountType] || p.discountType,
        usage: `${p.totalUsedCount || 0}${p.usageLimitTotal ? ` / ${p.usageLimitTotal}` : ""}`,
        expiryDate: p.expiryDate ? new Date(p.expiryDate).toISOString().substring(0, 10) : "—",
        active: p.active,
    }));

    return (
        <>
            <MetaData title="Admin: Promo Codes | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <div>
                    <Link to="/admin/site-content" className="text-sm text-primary-blue hover:underline">&larr; Back to Site Content</Link>
                    <h1 className="text-lg font-medium uppercase mt-1">Promo Codes</h1>
                </div>
                {canCreate && (
                    <Link to="/admin/promo-code/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Promo Code</Link>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full" style={{ height: 470 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    disableSelectIconOnClick
                    sx={{ boxShadow: 0, border: 0 }}
                />
            </div>
        </>
    );
};

export default PromoCodeTable;